import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api, { formatCurrency, formatDate } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function Dashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const nav = useNavigate();
  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(true);

  useEffect(() => {
    api.get('/dashboard').then(r=>{ setData(r.data.data); setLoad(false); }).catch(()=>setLoad(false));
  }, []);

  const greet = () => { const h=new Date().getHours(); return h<12?'Good morning':h<17?'Good afternoon':'Good evening'; };

  if (loading) return (
    <div className="stats-grid">
      {[1,2,3,4].map(i=><div key={i} className="skeleton" style={{ height:110,borderRadius:16 }}/>)}
    </div>
  );

  const d = data || {};
  const monthly = (d.monthly_revenue||[]).map(m=>({ month:m.month?.slice(5), revenue:parseFloat(m.revenue) }));

  const chartColors = theme === 'dark'
    ? { stroke:'#6c63ff', grid:'#2a2d38', tick:'#555a70', tooltip:'#16181f', border:'#2a2d38' }
    : { stroke:'#5b53f0', grid:'#e2e4ec', tick:'#9599b3', tooltip:'#ffffff', border:'#e2e4ec' };

  return (
    <div>
      <div style={{ marginBottom:22 }}>
        <h2 style={{ fontSize:21,fontWeight:800 }}>{greet()}, {user?.name?.split(' ')[0]} 👋</h2>
        <p style={{ color:'var(--text3)',fontSize:12.5,marginTop:3 }}>Here's your business snapshot for today.</p>
      </div>

      {(d.upcoming_renewals||[]).length>0 && (
        <div style={{ marginBottom:18 }}>
          {d.upcoming_renewals.slice(0,3).map(r=>(
            <div key={r.id} className="alert alert-warn">
              ⏰ <strong>{r.service_name}</strong> for <strong>{r.client_name}</strong> renews in <strong>{r.days_until}d</strong> — {formatDate(r.renewal_date)}
              <button className="btn btn-sm btn-warn" style={{ marginLeft:'auto' }} onClick={()=>nav('/renewals')}>View</button>
            </div>
          ))}
        </div>
      )}

      <div className="stats-grid">
        {[
          { icon:'🧾', label:'Total Invoices', value:d.invoices?.total||0, sub:formatCurrency(d.invoices?.total_amount), cls:'purple' },
          { icon:'✅', label:'Paid',   value:d.invoices?.paid?.count||0,   sub:formatCurrency(d.invoices?.paid?.amount),   cls:'green' },
          { icon:'⏳', label:'Unpaid', value:d.invoices?.unpaid?.count||0, sub:formatCurrency(d.invoices?.unpaid?.amount), cls:'yellow' },
          { icon:'🚨', label:'Overdue',value:d.invoices?.overdue?.count||0,sub:formatCurrency(d.invoices?.overdue?.amount),cls:'red' },
        ].map(s=>(
          <div key={s.label} className="stat-card" style={{ animationDelay:`${['purple','green','yellow','red'].indexOf(s.cls)*60}ms` }}>
            <div className={`stat-icon ${s.cls}`}>{s.icon}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid-2" style={{ marginBottom:20 }}>
        {/* Revenue Chart */}
        <div className="card">
          <div className="card-header"><span className="card-title">📈 Revenue (6 months)</span></div>
          {monthly.length>0 ? (
            <ResponsiveContainer width="100%" height={190}>
              <AreaChart data={monthly}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={chartColors.stroke} stopOpacity={0.25}/>
                    <stop offset="95%" stopColor={chartColors.stroke} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid}/>
                <XAxis dataKey="month" stroke={chartColors.tick} tick={{ fontSize:10.5 }}/>
                <YAxis stroke={chartColors.tick} tick={{ fontSize:10.5 }} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`}/>
                <Tooltip contentStyle={{ background:chartColors.tooltip, border:`1px solid ${chartColors.border}`, borderRadius:8, fontSize:12, color:'var(--text)' }} formatter={v=>[formatCurrency(v),'Revenue']}/>
                <Area type="monotone" dataKey="revenue" stroke={chartColors.stroke} strokeWidth={2} fill="url(#rev)"/>
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state" style={{ padding:40 }}><div style={{ fontSize:32 }}>📊</div><p>No revenue data yet</p></div>
          )}
        </div>

        {/* Quick stats */}
        <div className="card">
          <div className="card-header"><span className="card-title">📊 Overview</span></div>
          <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
            {[
              { label:'Total Clients',      value:d.clients||0,                   icon:'👥', color:'var(--info)' },
              { label:'Total Quotations',   value:d.quotations||0,                icon:'📋', color:'var(--accent)' },
              { label:'Upcoming Renewals',  value:(d.upcoming_renewals||[]).length, icon:'🔄', color:'var(--warn)' },
            ].map(item=>(
              <div key={item.label} style={{ display:'flex',alignItems:'center',gap:12,padding:'11px 13px',background:'var(--bg4)',borderRadius:'var(--r)' }}>
                <span style={{ fontSize:22 }}>{item.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11.5,color:'var(--text3)' }}>{item.label}</div>
                  <div style={{ fontSize:20,fontWeight:800,color:item.color }}>{item.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid-2">
        {/* Recent invoices */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Recent Invoices</span>
            <button className="btn btn-sm btn-secondary" onClick={()=>nav('/invoices')}>View all</button>
          </div>
          {(d.recent_invoices||[]).length===0 ? (
            <div className="empty-state" style={{ padding:28 }}><div style={{ fontSize:28 }}>🧾</div><p style={{ fontSize:12 }}>No invoices yet</p></div>
          ) : (
            <div>
              {d.recent_invoices.map(inv=>(
                <div key={inv.id} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 0',borderBottom:'1px solid var(--border2)' }}>
                  <div>
                    <div style={{ fontSize:12.5,fontWeight:600 }}>{inv.invoice_number}</div>
                    <div style={{ fontSize:11.5,color:'var(--text3)' }}>{inv.client_name}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:13,fontWeight:600 }}>{formatCurrency(inv.total)}</div>
                    <span className={`badge badge-${inv.status}`}>{inv.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="card">
          <div className="card-header"><span className="card-title">Recent Activity</span></div>
          {(d.recent_activity||[]).length===0 ? (
            <div className="empty-state" style={{ padding:28 }}><div style={{ fontSize:28 }}>📝</div><p style={{ fontSize:12 }}>No activity yet</p></div>
          ) : (
            <div>
              {d.recent_activity.map(a=>(
                <div key={a.id} style={{ display:'flex',gap:9,padding:'8px 0',borderBottom:'1px solid var(--border2)' }}>
                  <span style={{ fontSize:15,marginTop:1 }}>{a.action.includes('invoice')?'🧾':a.action.includes('quotation')?'📋':a.action.includes('client')?'👤':'📝'}</span>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontSize:12.5,color:'var(--text)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{a.description}</div>
                    <div style={{ fontSize:11,color:'var(--text3)',marginTop:1 }}>{new Date(a.created_at).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
